import { configureStore } from "@reduxjs/toolkit";
import { savedPlanIDs } from "ComparePlans/redux";
import { SavedPlanSaga } from "ComparePlans/Sagas";
import {
  formattedEmployeeDataApi,
  companyApplicationApi,
  companyContributionsApi,
  accountApi,
  pendingItemsApi,
  userExperienceFlagsApi,
  lifePlansApi,
  employerApplicationDocumentApi,
  longTermDisabilityPlansApi,
  shortTermDisabilityPlansApi,
  enrolledPolicyPlansApi,
  disabilityQuoteApi,
  renewalApi,
  employeeFormsApi,
} from "EmployerApplication/api";
import {
  accountId,
  companyPolicyId,
  pendingItems,
  resourceStates,
  employees,
  reduxFormReducerWithReset,
  ownersAsEmployeesReducer,
  costEstimationReducer,
  renewalPolicyReducer,
  isLoaded,
  companyProfileId,
  currentlyEnrolledCompanyPolicyId,
  companyProfilesReducer,
} from "EmployerApplication/Reducers";
import navigationSaga from "EmployerApplication/renewalNavigationSaga";
import {
  watchGetApplicationsMenuState,
  watchGetApplicationStatus,
  watchFetchEmployees,
  watchDeleteEmployee,
  watchLoadInitialState,
} from "EmployerApplication/Sagas";
import { START_REQUEST, UPDATE_ENTITIES } from "EntitiesApi/Actions";
import { entitiesReducer } from "EntitiesApi/Reducers";
import { rootSaga as apiSaga } from "EntitiesApi/Sagas";
import { createBrowserHistory as createHistory } from "history";
import { largeGroupSurveyApi } from "LargeGroupIntent/api";
import { INITIAL_STATE_LOAD_SUCCESS } from "QuotePage/Actions";
import { favoritePlanIDs, FavoritePlanSaga } from "QuotePage/Components/FavoritePlan";
import { sort, filters, asyncInitialState, planTypes, recommendationSurvey, recommendedPlans } from "QuotePage/Reducers";
import {
  FiltersSaga,
  RefreshDentalPlans,
  RefreshAllDentalPlans,
  RefreshVisionPlans,
  RefreshMecPlans,
  RefreshMvpPlans,
  RecommendationSurveySaga,
  watchRequestInitialData,
  watchRequestPlanSuite,
  watchRequestEmployeeSync,
} from "QuotePage/Sagas";
import { combineReducers } from "redux";
import honeybadgerMiddleware from "Redux/Middleware/honeybadgerMiddleware";
import {
  dentalPlans,
  loadingDentalPlans,
  allDentalPlans,
  isLoadingAllDentalPlans,
  medicalPlans,
  mecPlans,
  mvpPlans,
  visionPlans,
  loadingVisionPlans,
  lifePlans,
  loadingLifePlans,
  carriersInRegion,
  isShowingExtraPlans,
} from "Redux/Reducers";
import createSagaMiddleware from "redux-saga";
import { fork } from "redux-saga/effects";
import _ from "underscore";
import { allowReplacement } from "Utils/allowReplacement";
import companyIdTokenStore from "Utils/companyIdTokenStore";
import { companyTokenString } from "Utils/companyToken";
import { baseUrl } from "ZeroRuby/urls";

import { modalReducer } from "Cobalt/Modal";
import { abTestReducer } from "Common/abTestUtils/redux";
import { watchAddPlanToCart, cart, relatedCartPlanIds } from "Common/CartService";
import { employeeProfileReducer, employeeProfileOverallErrorReducer } from "Common/EnterAges/Reducers";
import { EmployeeProfileSaga, validateOnChange as EmployeeProfileValidateOnChange } from "Common/EnterAges/Sagas";
import { flipperReducer } from "Common/flipperUtils/redux";
import { newCostBreakdownReducer } from "Common/NewCostBreakdownModal/Reducers";
import { watchInitializePlans, watchRequestPlans, allPlans, loadingPlans, RECEIVE_PLANS } from "Common/PlanLoader";
import { redirectOnErrorMiddleware } from "Common/Redux/middleware";
import reduxTokenAuth from "Common/TokenAuth/Reducers";

import { watchInitialStateLoadFinished, watchRefreshQuote, watchEmployeeProfileChanged, watchCartUpdateSuccessInQuotePage } from "./ServicesFacadeSagas";

function* rootSaga() {
  yield [
    fork(apiSaga),
    fork(navigationSaga),
    fork(EmployeeProfileSaga),
    fork(EmployeeProfileValidateOnChange),
    fork(FavoritePlanSaga),
    fork(FiltersSaga),
    fork(RecommendationSurveySaga),
    fork(RefreshAllDentalPlans),
    fork(RefreshDentalPlans),
    fork(RefreshVisionPlans),
    fork(RefreshMecPlans),
    fork(RefreshMvpPlans),
    fork(SavedPlanSaga),
    fork(watchAddPlanToCart),
    fork(watchCartUpdateSuccessInQuotePage),
    fork(watchDeleteEmployee),
    fork(watchEmployeeProfileChanged),
    fork(watchFetchEmployees),
    fork(watchGetApplicationsMenuState),
    fork(watchGetApplicationStatus),
    fork(watchInitializePlans),
    fork(watchInitialStateLoadFinished),
    fork(watchLoadInitialState),
    fork(watchRefreshQuote),
    fork(watchRequestEmployeeSync),
    fork(watchRequestInitialData),
    fork(watchRequestPlans),
    fork(watchRequestPlanSuite),
  ];
}

const sagaMiddleware = createSagaMiddleware();
export const companyIdToken = companyIdTokenStore();

export const companyPrefixURLPath = `${baseUrl}${companyTokenString()}`;
export const history = createHistory({
  basename: companyPrefixURLPath,
});

/// Returns a reducer that responds to no actions and has the given initial
/// value.
const constantReducer = (initial) => (x) => (typeof x === "undefined" ? initial : x);

// Allows initial state to be updated asynchronously.
const wrapperReducer = (appReducer) => (state, action) => {
  switch (action.type) {
    case INITIAL_STATE_LOAD_SUCCESS: {
      const abTests = {
        ...state.abTests,
        ...action.initialState.abTests,
      };

      return appReducer({ ...state, ...action.initialState, abTests }, action);
    }
    default:
      return appReducer(state, action);
  }
};

const reducerSlices = {
  abTests: abTestReducer,
  flipper: flipperReducer,
  accountId,
  allCarriersMaxOfferedPlans: constantReducer({}),
  allDentalPlans,
  allPlans,

  asyncInitialState,
  carriersInRegion,
  cart,
  city: constantReducer(null),
  companyPolicyId,
  companyProfileId,
  currentlyEnrolledCompanyPolicyId,
  isLoaded,
  companyProfile: companyProfilesReducer,
  costEstimation: costEstimationReducer,
  dentalPlans,
  employeeProfile: employeeProfileReducer,
  employeeProfileOverallError: employeeProfileOverallErrorReducer,
  employees,
  entities: entitiesReducer({
    companyProfiles: {},
    recommendationSurveys: {},
    companyApplications: {},
    companyPolicies: {},
    employeeForms: {},
    companyContributions: {},
  }),

  favoritePlanIDs,
  filters,
  form: reduxFormReducerWithReset,
  isLoadingAllDentalPlans,
  isShowingExtraPlans,
  lifePlans,
  loadingDentalPlans,
  loadingPlans,
  loadingVisionPlans,
  loadingLifePlans,
  medicalPlanEnrolledId: constantReducer(null),
  medicalPlanRenewalId: constantReducer(null),
  medicalPlans,
  mecPlans,
  mvpPlans,
  newCostBreakdown: newCostBreakdownReducer,
  nextQuarterPricingAvailable: constantReducer(false),
  openModal: modalReducer,
  ownersAsEmployees: ownersAsEmployeesReducer,
  pendingItems,
  planPackages: constantReducer([]),
  planTypes,
  recommendationSurvey,
  recommendedPlans,
  relatedCartPlanIds,
  renewalPolicy: renewalPolicyReducer,
  resourceStates,
  savedPlanIDs,
  siteConfig: constantReducer({
    branding: "default",
  }),

  sort,
  state: constantReducer(null),
  trackingParams: constantReducer({}),
  userExperienceFlags: constantReducer({}),
  visionPlans,
  reduxTokenAuth,
  [companyApplicationApi.reducerPath]: companyApplicationApi.reducer,
  [formattedEmployeeDataApi.reducerPath]: formattedEmployeeDataApi.reducer,
  [companyContributionsApi.reducerPath]: companyContributionsApi.reducer,
  [accountApi.reducerPath]: accountApi.reducer,
  [pendingItemsApi.reducerPath]: pendingItemsApi.reducer,
  [userExperienceFlagsApi.reducerPath]: userExperienceFlagsApi.reducer,
  [lifePlansApi.reducerPath]: lifePlansApi.reducer,
  [employerApplicationDocumentApi.reducerPath]: employerApplicationDocumentApi.reducer,
  [longTermDisabilityPlansApi.reducerPath]: longTermDisabilityPlansApi.reducer,
  [shortTermDisabilityPlansApi.reducerPath]: shortTermDisabilityPlansApi.reducer,
  [enrolledPolicyPlansApi.reducerPath]: enrolledPolicyPlansApi.reducer,
  [disabilityQuoteApi.reducerPath]: disabilityQuoteApi.reducer,
  [largeGroupSurveyApi.reducerPath]: largeGroupSurveyApi.reducer,
  [renewalApi.reducerPath]: renewalApi.reducer,
  [employeeFormsApi.reducerPath]: employeeFormsApi.reducer,
};

export const rootReducer = wrapperReducer(allowReplacement(combineReducers(reducerSlices)));

function getStore(preloadedState) {
  const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [UPDATE_ENTITIES, START_REQUEST, RECEIVE_PLANS],
          ignoredActionPaths: ["payload.coverageStartDate", "payload.hiredAt", "payload.bornAt", "meta.baseQueryMeta.request", "meta.baseQueryMeta.response"],
          ignoredPaths: ["entities.employeeForms", "entities.employeeDependents", "allPlans", "entities.companyProfiles", "entities.companyPolicies"],
        },
        immutableCheck: {
          ignoredPaths: ["allPlans"],
        },
      })
        .concat(sagaMiddleware)
        .concat(formattedEmployeeDataApi.middleware)
        .concat(companyApplicationApi.middleware)
        .concat(companyContributionsApi.middleware)
        .concat(accountApi.middleware)
        .concat(pendingItemsApi.middleware)
        .concat(userExperienceFlagsApi.middleware)
        .concat(lifePlansApi.middleware)
        .concat(employerApplicationDocumentApi.middleware)
        .concat(longTermDisabilityPlansApi.middleware)
        .concat(shortTermDisabilityPlansApi.middleware)
        .concat(honeybadgerMiddleware)
        .concat(enrolledPolicyPlansApi.middleware)
        .concat(disabilityQuoteApi.middleware)
        .concat(largeGroupSurveyApi.middleware)
        .concat(redirectOnErrorMiddleware)
        .concat(renewalApi.middleware)
        .concat(employeeFormsApi.middleware),
    preloadedState,
    devTools: {
      maxAge: 200,
    },
  });

  sagaMiddleware.run(rootSaga);
  return store;
}

export default getStore;
